import dayjs, { type Dayjs } from "dayjs";
import type { TeleconsultoriaActor, TeleconsultoriaGuest } from "../../types";
import {
  DEFAULT_INVITE_KIND,
  INVITE_APPOINTMENT_IDENTIFIER_SYSTEM,
  INVITE_INPUT,
  INVITE_INPUT_SYSTEM,
  INVITE_RESPONSE_TASK_CODE,
  INVITE_TASK_CODE,
  INVITE_TASK_CODE_SYSTEM,
  type InviteKind,
} from "../constants";

/**
 * Task de convite de teleconsultoria (code próprio em `task-types`).
 * Devolve boolean (não type predicate) para não estreitar a Task do chamador
 * para `never` depois de um early return.
 */
export function isTeleconsultoriaInviteTask(task: unknown): boolean {
  if (!task || typeof task !== "object") return false;
  const codings = (task as fhir5.Task).code?.coding ?? [];
  return codings.some(
    (coding) =>
      coding.system === INVITE_TASK_CODE_SYSTEM && coding.code === INVITE_TASK_CODE,
  );
}

/** Notificação de resposta ao convite (aceito/recusado), destinada a quem convidou. */
export function isInviteResponseTask(task: unknown): boolean {
  if (!task || typeof task !== "object") return false;
  const codings = (task as fhir5.Task).code?.coding ?? [];
  return codings.some(
    (coding) =>
      coding.system === INVITE_TASK_CODE_SYSTEM && coding.code === INVITE_RESPONSE_TASK_CODE,
  );
}

/** Convite aguardando resposta cujo prazo (`restriction.period.end`) já passou. */
export function isInviteExpired(task: fhir5.Task, now: Dayjs = dayjs()): boolean {
  if (task.status !== "requested") return false;
  const end = task.restriction?.period?.end;
  return Boolean(end && dayjs(end).isBefore(now));
}

/** Aguardando resposta e dentro do prazo. */
export function isInvitePending(task: fhir5.Task, now: Dayjs = dayjs()): boolean {
  return task.status === "requested" && !isInviteExpired(task, now);
}

/**
 * Id do Appointment de origem: `identifier` (NamingSystem scheduling-appointment-id).
 * Fallback `focus` só para convites criados antes (quando ainda era referência).
 */
export function inviteAppointmentId(task: fhir5.Task): string {
  const fromIdentifier = task.identifier?.find(
    (identifier) => identifier.system === INVITE_APPOINTMENT_IDENTIFIER_SYSTEM,
  )?.value;
  if (fromIdentifier) return fromIdentifier;
  const reference = task.focus?.reference || "";
  return reference.startsWith("Appointment/") ? reference.split("/")[1] || "" : "";
}

/** Location do atendimento de origem (`Task.location`), usada na busca do Appointment. */
export function inviteLocationReference(task: fhir5.Task): string | undefined {
  const reference = task.location?.reference;
  return reference?.startsWith("Location/") ? reference : undefined;
}

function findInput(task: fhir5.Task, inputCode: string) {
  return task.input?.find((item) =>
    item.type?.coding?.some(
      (coding) => coding.system === INVITE_INPUT_SYSTEM && coding.code === inputCode,
    ),
  );
}

function inputReference(task: fhir5.Task, inputCode: string): TeleconsultoriaActor | undefined {
  const input = findInput(task, inputCode);
  const reference = input?.valueReference?.reference;
  if (!reference) return undefined;
  return { reference, display: input?.valueReference?.display || "" };
}

/** Convidado gravado no convite (Practitioner + HCS). */
export function inviteGuestFromTask(task: fhir5.Task): TeleconsultoriaGuest | undefined {
  const practitioner = inputReference(task, INVITE_INPUT.guestPractitioner);
  if (!practitioner) return undefined;
  const healthcareService = inputReference(task, INVITE_INPUT.guestHealthcareService);
  return { practitioner, ...(healthcareService ? { healthcareService } : {}) };
}

/** Tipo do convite (input `invite-kind`). Sem o input = convite de atendimento aberto. */
export function inviteKindFromTask(task: fhir5.Task): InviteKind {
  const value = task.input?.find((item) =>
    item.type?.coding?.some(
      (coding) => coding.system === INVITE_INPUT_SYSTEM && coding.code === INVITE_INPUT.inviteKind,
    ),
  )?.valueCode;
  return value === "scheduled" || value === "now" ? value : DEFAULT_INVITE_KIND;
}

/** Paciente do convite (só para exibição): `valueString`; convites antigos usavam `valueReference`. */
export function invitePatientDisplay(task: fhir5.Task): string {
  const input = findInput(task, INVITE_INPUT.patient);
  return input?.valueString || input?.valueReference?.display || "Paciente";
}

/** Rótulo do status do convite para a UI. */
export function inviteStatusLabel(task: fhir5.Task, now: Dayjs = dayjs()): string {
  if (isInviteExpired(task, now)) return "Expirado";
  if (task.status === "requested") return "Pendente";
  if (task.status === "accepted") return "Aceito";
  if (task.status === "rejected") return "Recusado";
  if (task.status === "cancelled") return "Cancelado";
  return task.status || "Pendente";
}
